/**
 * HereMenu brand logo. Single source of truth so the mark, wordmark and
 * lockup never drift between login screen, sidebar, empty states and any
 * future surfaces. The geometry is the same as `/public/favicon.svg` and
 * `/public/logo.svg`; only those static files are needed for HTML
 * referrers (browser tab, OG image, etc.).
 *
 * Why inline SVG instead of <img src="/logo.svg">: text rendering picks up
 * the page's font (Inter) consistently with the rest of the UI, and the
 * mark inherits Tailwind's color tokens if we ever theme it.
 */
type Size = "sm" | "md" | "lg" | "xl";
type Variant = "full" | "mark";

const sizes: Record<Size, { mark: number; text: string }> = {
  sm: { mark: 20, text: "text-sm" },
  md: { mark: 32, text: "text-lg" },
  lg: { mark: 44, text: "text-2xl" },
  xl: { mark: 56, text: "text-3xl" },
};

interface LogoProps {
  size?: Size;
  variant?: Variant;
  className?: string;
}

export function Logo({ size = "md", variant = "full", className = "" }: LogoProps) {
  const s = sizes[size];
  return (
    <div className={`inline-flex items-center gap-2.5 ${className}`}>
      <LogoMark px={s.mark} />
      {variant === "full" && (
        <span className={`font-bold tracking-tight leading-none ${s.text}`}>
          <span className="text-slate-900">here</span>
          <span className="text-[#047857]">menu</span>
        </span>
      )}
    </div>
  );
}

function LogoMark({ px }: { px: number }) {
  return (
    <svg
      width={px}
      height={px}
      viewBox="0 0 100 100"
      xmlns="http://www.w3.org/2000/svg"
      className="shrink-0"
      aria-label="HereMenu"
      role="img"
    >
      <rect x="6" y="6" width="88" height="88" rx="20" fill="#047857" />
      <rect x="20" y="22" width="60" height="56" rx="6" fill="#fafaf9" />
      <rect x="28" y="32" width="44" height="6" rx="2" fill="#0f172a" />
      <rect x="28" y="46" width="36" height="4" rx="2" fill="#334155" />
      <rect x="28" y="56" width="44" height="4" rx="2" fill="#334155" />
      <rect x="28" y="66" width="28" height="4" rx="2" fill="#334155" />
    </svg>
  );
}
